import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CollectionCardMiniModule, CollectionCardModule } from '@app/collections';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { AbstractCollectionPane } from '@app/library/panes/models/abstract-collection-pane';
import { EmptyComponent, EmptyProps } from '@core/components/empty/empty.component';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { SpinnersModule } from '@core/components/spinners/spinners.module';
import { Collection, CollectionDetailsType, CollectionItemDetailsType } from 'lingo2-models';
import { InViewportModule } from 'ng-in-viewport';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-favorites-collections',
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    SpinnersModule,

    InViewportModule,
    CollectionCardMiniModule,
    ModalModule,
    MobileModalModule,
    CollectionCardModule,
    EmptyComponent,
  ],
  templateUrl: './favorites-collections.component.html',
  styleUrls: ['./favorites-collections.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FavoritesCollectionsComponent extends AbstractCollectionPane {
  protected location = inject(Location);

  public showModal = false;
  public openedCardIndex: number = null;
  public item: Collection;
  public itemDetails: CollectionItemDetailsType[] = ['id', 'content_id', 'is_open', 'sort_order', 'content'];

  private originalUrl: string;

  public emptyObj: Partial<EmptyProps> = {
    text: 'favorites.empty-message',
    image: 'favorites',
    link_label: 'favorites/collections.empty',
    link: '/collections',
    link2: '/collections',
  };

  protected onContentLoaded() {
    this.isVisible = true;
  }

  hasContent(): boolean {
    return true;
  }

  public onInViewportBottomChange(reached) {
    // logger.debug('FavoritesCollectionsComponent::', reached);
    if (reached.visible) {
      this.loadNextPage();
    }
  }

  protected get details(): CollectionDetailsType[] {
    return [
      'id',
      'slug',
      'author:sm',
      'type',
      'title',
      'category',
      'excerpt',
      'subject',
      'language',
      'multi_cover:lg:md',
      'price_tiers',
      'discount',
      'description',
      'access',
      'stats',
      'visit_info',
      'options',
      'changed_at',
      'moderation_status',
      'moderation_message',
    ];
  }

  public onFavoriteChanged(index: number) {
    this.collections.splice(index, 1);
  }

  public trackContentByFn(index, item: Collection): string {
    return item.id;
  }

  public onOpenCard(collection: Collection) {
    this.openedCardIndex = this.collections.findIndex((el) => el.id === collection.id);
    this.item = collection;
    this.loadCollectionItems(collection);
    const url = this.collectionRoute(this.item);
    const currentUrl = this.location.path(false);
    if (currentUrl !== url) {
      if (!this.originalUrl) {
        this.originalUrl = currentUrl;
      }
      this.location.replaceState(url);

      // отправка событий в аналитику для имитации навигации по страницам
      this.analytics.pageView(url, currentUrl);
    }
    this.showModal = !!this.item;
  }

  protected loadCollectionItems(item: Collection) {
    this.collectionsService
      .getItemsById(item.id, this.itemDetails)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (items) => {
          this.item = new Collection(item);
          this.item.items = items.results;
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public onAction() {
    this._pane.loaded = false;
    this.loadPane();
    this.markForCheck();
  }

  public onCloseCard() {
    this.openedCardIndex = null;
    this.item = null;
    this.showModal = false;

    if (this.originalUrl) {
      this.location.replaceState(this.originalUrl);
    }
  }
}
